import {
  createRouter as cr,
  createWebHashHistory,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";

import { useAuthStore } from "@/stores/auth";
import { useToastsStore } from "@/stores/toasts";
import AccountView from "@/views/account/AccountView.vue";
import DeviceTokenView from "@/views/authentication/DeviceTokenView.vue";
import LoginSuccessView from "@/views/authentication/LoginSuccessView.vue";
import LoginView from "@/views/authentication/LoginView.vue";
import LogoutView from "@/views/authentication/LogoutView.vue";
import EstimatorReportComparisonBuilderView from "@/views/comparison/EstimatorReportComparisonBuilderView.vue";
import EstimatorReportComparisonView from "@/views/comparison/EstimatorReportComparisonView.vue";
import CrashView from "@/views/CrashView.vue";
import DispatchView from "@/views/dispatch/DispatchView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import OnboardingView from "@/views/onboarding/OnboardingView.vue";
import ProjectDetailView from "@/views/projects/ProjectDetailView.vue";
import ProjectsListView from "@/views/projects/ProjectsListView.vue";
import CrossValidationReportView from "@/views/reports/CrossValidationReportView.vue";
import EstimatorReportView from "@/views/reports/EstimatorReportView.vue";
import WorkspaceDetailView from "@/views/workspaces/WorkspaceDetailView.vue";

declare module "vue-router" {
  interface RouteMeta {
    requiresAuthentication: boolean;
  }
}

export enum RouteNames {
  Dispatch = "dispatch",
  Projects = "projects",
  ProjectDetail = "project_detail",
  EstimatorReportDetail = "estimator_report_detail",
  CrossValidationReportDetail = "cross_validation_report_detail",
  ComparisonBuilder = "comparison_builder",
  Comparison = "comparison",
  WorkspaceDetail = "workspace_detail",
  Onboarding = "onboarding",
  Account = "account",
  Login = "login",
  LoginSuccess = "login_success",
  DeviceToken = "device_token",
  Logout = "logout",
  NotFound = "not_found",
  Crash = "crash",
  Components = "components",
}

export enum ProjectDetailSections {
  EstimatorReports = "estimators",
  CrossValidationReports = "cross-validations",
  Comparisons = "comparisons",
}

export function isValidProjectDetailSection(section: string): boolean {
  return Object.values(ProjectDetailSections).includes(section as ProjectDetailSections);
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: RouteNames.Dispatch,
    component: DispatchView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId",
    name: RouteNames.Projects,
    component: ProjectsListView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName",
    redirect: (to) => ({
      name: RouteNames.ProjectDetail,
      params: {
        workspacePublicId: to.params.workspacePublicId as string,
        projectName: to.params.projectName as string,
        section: ProjectDetailSections.EstimatorReports,
      },
    }),
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/:section/:itemId?",
    name: RouteNames.ProjectDetail,
    component: ProjectDetailView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.EstimatorReports}/:reportId`,
    name: RouteNames.EstimatorReportDetail,
    component: EstimatorReportView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: `/:workspacePublicId/:projectName/${ProjectDetailSections.CrossValidationReports}/:reportId`,
    name: RouteNames.CrossValidationReportDetail,
    component: CrossValidationReportView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/builder",
    name: RouteNames.ComparisonBuilder,
    component: EstimatorReportComparisonBuilderView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/:workspacePublicId/:projectName/comparisons/:comparisonId",
    name: RouteNames.Comparison,
    component: EstimatorReportComparisonView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/workspace/:workspaceId",
    name: RouteNames.WorkspaceDetail,
    component: WorkspaceDetailView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/account",
    name: RouteNames.Account,
    component: AccountView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/login",
    name: RouteNames.Login,
    component: LoginView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/login/success",
    name: RouteNames.LoginSuccess,
    component: LoginSuccessView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/device",
    name: RouteNames.DeviceToken,
    component: DeviceTokenView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/logout",
    name: RouteNames.Logout,
    component: LogoutView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/not-found",
    name: RouteNames.NotFound,
    component: NotFoundView,
    meta: {
      requiresAuthentication: false,
    },
  },
  {
    path: "/onboarding",
    name: RouteNames.Onboarding,
    component: OnboardingView,
    meta: {
      requiresAuthentication: true,
    },
  },
  {
    path: "/crash",
    name: RouteNames.Crash,
    component: CrashView,
    meta: {
      requiresAuthentication: false,
    },
  },
];

if (import.meta.env.DEV) {
  routes.push({
    path: "/components",
    name: RouteNames.Components,
    component: () => import("@/views/components/ComponentsView.vue"),
    meta: {
      requiresAuthentication: false,
    },
  });
}

function createRouter() {
  /**
   * To handle PR deployment in the same bucket
   * we use anchor navigation if the base is not empty.
   */
  const history =
    import.meta.env.BASE_URL !== "/"
      ? createWebHashHistory(import.meta.env.BASE_URL)
      : createWebHistory(import.meta.env.BASE_URL);
  const router = cr({
    history,
    routes,
  });
  router.beforeEach(async (to) => {
    // Access store inside the guard when Pinia is initialized
    const authStore = useAuthStore();

    // refresh the token if needed
    if ("error" in to.query) {
      const toastsStore = useToastsStore();
      switch (to.query["error"]) {
        case "invalid_state_exception": {
          toastsStore.addToast(
            "Something went wrong during authentication, please try again.",
            "error"
          );
          break;
        }
        default: {
          break;
        }
      }
    }
    const isUserAuthenticated = await authStore.ensureUserIsAuthenticated();
    if (to.meta.requiresAuthentication && !isUserAuthenticated) {
      return { name: RouteNames.Dispatch, query: { next: to.fullPath } };
    }

    if (authStore.currentUser === null && authStore.isAuthenticated === true) {
      await authStore.fetchMe();
    }
  });
  return router;
}

export const router = createRouter();
